import * as React from 'react';
import { useSelector } from 'react-redux';
import { PublishTerms } from './PublishTerms';
import { withProvider } from '../../redux';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger/shared';
import { IConfig } from '@cian/config/shared';
import { selectIsSubagentLimitExceed, selectPublishTermsStatus, selectServiceIds } from '../../selectors/publishTerms';
import { EPublishTermsStatus } from '../../redux/reducers';
import { ApplicationContext } from '../ApplicationContext';
import { selectIsIdentificationNeeded, selectIsPublishToHiddenBase } from '../../selectors/publishAvailable';
import { NeedIdentificationMobile } from '../NeedIdentificationMobile';
import { Device } from '../common/Device';
import { HiddenBaseMobile } from '../HiddenBaseMobile';
import { ReactErrorLog } from '../common/ReactErrorLog';
import { ITelemetryLogger } from '@cian/telemetry/shared';

interface IPublishTermsContainerProps {
  httpApi: IHttpApi;
  logger: ILogger;
  config: IConfig;
  telemetry: ITelemetryLogger;
}

export const Container = (props: IPublishTermsContainerProps) => {
  const { httpApi, logger, config, telemetry } = props;

  const status = useSelector(selectPublishTermsStatus);
  const isSubagentLimitExceed = useSelector(selectIsSubagentLimitExceed);
  const isIdentificationNeeded = useSelector(selectIsIdentificationNeeded);
  const isPublishToHiddenBase = useSelector(selectIsPublishToHiddenBase);
  const serviceIds = useSelector(selectServiceIds);

  const isInitial = status === EPublishTermsStatus.initial;
  const isLoading = status === EPublishTermsStatus.pending;
  const isSuccess = status === EPublishTermsStatus.success;
  const isError = status === EPublishTermsStatus.fail;

  const handleRenderSuccess = React.useCallback(() => {
    telemetry.increment('react.publish-terms.render.success');
  }, [telemetry]);

  const handleRenderError = React.useCallback(
    (error: Error, errorInfo: React.ErrorInfo) => {
      telemetry.increment('react.publish-terms.render.fail');

      logger.debug('Ошибка рендеринга услуг размещения', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    },
    [logger, telemetry],
  );

  if (isIdentificationNeeded) {
    // десктопный компонент рендерится в сегменте внутри SummaryDesktop
    return <Device mobile={<NeedIdentificationMobile />} />;
  }

  if (isPublishToHiddenBase) {
    // десктопный компонент рендерится в сегменте внутри SummaryDesktop
    return <Device mobile={<HiddenBaseMobile />} />;
  }

  if (isInitial || isSuccess && serviceIds.length === 0 && !isSubagentLimitExceed) {
    return null;
  }

  const context = { httpApi, logger, config };

  return (
    <ApplicationContext.Provider value={context}>
      <ReactErrorLog onSuccess={handleRenderSuccess} onError={handleRenderError}>
        <PublishTerms isLoading={isLoading} isError={isError} isSubagentLimitExceed={isSubagentLimitExceed} />
      </ReactErrorLog>
    </ApplicationContext.Provider>
  );
};

export const PublishTermsContainer = withProvider(Container);
